<template>
    <div class="release-preview"
        :class="{
            row: props.row,
            invert: props.invert,
            light: props.light,
            disabled: props.disabled,
            skeleton: props.skeleton
        }"
    >
        <div>
            <div class="cover" :class="{ disabled: false }">
                <Img v-if="cover?.resized || defaultCover?.resized || props.skeleton"
                    :preview="props.skeleton ? null : cover?.resized || defaultCover?.resized"
                    :original="props.skeleton ? null : cover?.original || defaultCover?.original"
                    :skeleton="true"
                    aspectRatio="1 / 1"
                />

                <div class="buttons" v-if="props.playable">
                    <IconButton
                        @click.stop="open"
                        class="open size-s tertiary invert"
                        icon="preview-list" sprite="navigation"
                    />

                    <AudioToggle
                        :state="state"
                        @play="play"
                        @pause="pause"
                    />
                </div>

            </div>
            <State v-if="!props.row && props.showState && props.item?.state" 
                :state="props.item?.state"
                class="solid" />
        </div>
        <div>
            <div>
                <span class="title"
                    :class="{ full: props.item?.fromProject }"
                >
                    {{ props.item?.title || t('releases.empty-name') }}
                </span>
                <span class="from" v-if="props.item?.fromProject">
                    {{ t('releases.from') }} {{ props.item?.fromProject }}
                </span>

                <State v-if="props.row && props.showState && props.item?.state" 
                    :state="props.item?.state"
                />
            </div>
            <div>
                <ContributorsPreview v-if="props.item?.contributors" :items="props.item?.contributors" :invert="props.invert" />

                <Button v-else-if="props.skeleton"
                    class="size-s tertiary gray link"
                    name="Artists"
                />
                
                <span>
                    <span v-if="props.item.releaseDate" class="year">
                        {{ moment(props.item.releaseDate).year() }}
                    </span>
                    
                    <ReleaseType :trackCount="props.item.trackCount" class="release-type"/>
                </span>
            </div>
        </div>

        <div v-if="props.showKebab">
            <IconButton
                @click.stop="openKebab"
                class="size-s tertiary"
                icon="more-vertical" sprite="navigation"
            />
        </div>
    </div>
</template>

<script setup>
    import { onMounted, reactive, computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import moment from 'moment';
    import Button from '🔗/components/ui/Button.vue';
    import IconButton from '🔗/components/ui/IconButton.vue';
    import Img from '🔗/components/ui/Img.vue';
    import AudioToggle from '🔗/components/ui/AudioToggle.vue';
    import ReleaseType from '🔗/components/ui/ReleaseType.vue';
    import ContributorsPreview from '🔗/components/ui/ContributorsPreview.vue';
    // import api from '🔗/api';
    import stores from '@/stores';
    import State from '🔗/components/ui/State.vue';

    const player = stores.player();
    const modals = stores.modals();


    const emit = defineEmits(['change']);

    const { t } = useI18n();

    const props = defineProps({
        item: { type: Object, default: () => ({}) },
        showState: { type: Boolean, default: false },
        row: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        light: { type: Boolean, default: false },
        playable: { type: Boolean, default: true },
        showKebab: { type: Boolean, default: false },
        skeleton: { type: Boolean, default: false },
        invert: { type: Boolean, default: false }
    });

    const cover = computed(() => {
        let url;
        if (props.item?.cover instanceof File) {
            url = URL.createObjectURL(props.item.cover);
        }

        return url ? {
            resized: url,
            cover: url,
            original: url
        } : props.item?.cover;
    });

    const data = reactive({
    });


    const defaultCover = computed(() => {
        return api.releases().defaultCover;
    });

    const openKebab = () => {
        modals.open('release-kebab', { quietClose: true, data: { id: props.item?.id } })
    }

    const open = () => {
        if (!props.item?.id) {
            return;
        }

        modals.reopen('release', {
            data: props.item?.id,
            popover: true
        })
    }

    const state = computed(() => {
        if (!player.panes.default || player.panes.default._queue?.id !== props.item?.id) {
            return;
        }

        return player.panes.default._state;
    });

    const pause = async () => {
        player.release.pause(props.item.id);
    }

    const play = async () => {
        player.release.play(props.item.id);
    }
</script>

<style lang="scss" scoped>
    .release-preview {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        width: 100%;
        overflow: hidden;
        & > div {
            &:first-child {
                display: flex;
                flex-direction: column;
                gap: 8px;
                position: relative;

                .cover {
                    width: 146px;
                    height: 146px;
                    position: relative;

                    .skeleton-wrapper,
                    img {
                        width: 100%;
                        height: 100%;
                        border-radius: 10px;
                        object-fit: cover;
                        overflow: hidden;
                    }

                    .buttons {
                        position: absolute;
                        // top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        display: flex;
                        flex-direction: row;
                        gap: 4px;
                        padding: 8px;
                        height: auto;
                        justify-content: end;
                        align-items: center;
                        filter: drop-shadow(1px 2px 2px var(--dark-80));
                        .open {
                            opacity: 0;
                            transition: opacity .2s;
                        }
                        .audio-toggle {
                            width: 34px;
                            height: 34px;
                            background: transparent;
                        }
                    }


                    &:hover {
                        .buttons {
                            .open {
                                opacity: 1;
                            }
                            .audio-toggle {
                                opacity: 1;
                            }
                        }
                    }


                    &.disabled {
                        filter: grayscale(1);
                        .skeleton-wrapper,
                        img {
                            cursor: default;
                        }
                    }
                }

                .state {
                    position: absolute;
                    right: 8px;
                    top: 8px;
                }
            }
            &:nth-child(2) {
                display: flex;
                flex-direction: column;
                gap: 4px;
                flex: 1;
                min-width: 0;

                & > div {
                    display: flex;
                    flex-direction: row;
                    align-items: baseline;
                    gap: 4px;
                    min-width: 0;

                    &:first-child {
                        flex-wrap: nowrap;
                    }
                    &:last-child {
                        flex-direction: column;
                        width: 100%;
                        span {
                            display: flex;
                            flex-direction: row;
                            gap: 4px;
                        }
                    }

                    .title {
                        font: var(--font-body-1);
                        color: var(--dark-500);
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        overflow: hidden;
                        &.full {
                            flex-shrink: 0;
                        }
                    }

                    .release-type,
                    .year {
                        font: var(--font-caption);
                        color: var(--gray-500);
                    }

                    .from {
                        font: var(--font-body-2);
                        white-space: nowrap;
                        color: var(--gray-80);
                        // flex-shrink: 0;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        // flex: 1;
                    }

                    .state {
                        margin-left: 4px;
                    }
                }
            }
            &:nth-child(3) {
                display: flex;
                flex-direction: column;
                flex-shrink: 0;
            }
        }

        &.disabled {
            opacity: .4;
            filter: grayscale(1);
            cursor: default;
        }

        &.row {
            flex-direction: row;
            & > div {
                &:first-child {
                    .cover {
                       width: 80px;
                       height: 80px;
                       .buttons {
                            top: 0;
                            height: auto;
                            padding: 0;

                            .open {
                                display: none;
                            }

                            .audio-toggle {
                                position: absolute;
                                top: 0;
                                left: 0;
                                right: 0;
                                bottom: 0;
                                width: 100%;
                                height: 100%;
                            }
                       }
                    }

                    .state {
                    }
                }
                &:nth-child(2) {
                    & > div {
                        &:first-child {
                        }
                        &:last-child {
                            align-items: start;
                        }

                        
                    }
                }
            }

            &.light {
                align-items: center;
                & > div {
                    &:first-child {
                        .cover {
                            width: 68px;
                            height: 68px;
                        }
                    }
                }

                & > div:nth-child(2) {
                    gap: 4px;
                    & > div:nth-child(3) {
                        display: none;
                    }
                }
            }
        }


        &.invert {
            & > div:nth-child(2) {
                & > div {
                    .title {
                        color: var(--white-500);           
                    }
                }
            }
        }

        &.skeleton {
            & > div:nth-child(2) {
                & > div {
                    position: relative !important;
                    color: transparent !important;
                    @include skeleton;

                    &:nth-child(1) {
                        max-width: 120px;
                        .title {
                            opacity: 0;
                        }
                    }
                    &:nth-child(2) {
                        max-width: 60px;
                        button {
                            opacity: 0;
                        }
                    }
                }

            }
        }
    }
</style>
